import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import {
  finalize,
  Subject,
  takeUntil,
  BehaviorSubject,
  filter,
  Observable,
  of,
} from 'rxjs';
import { StrategyItem, StrategyQueryParams } from '../strategy.interface';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../../../../../../components/confirm-dialog/confirm-dialog.component';
import { ManageBehaviorStrategiesService } from '../manage-behavior-strategies.service';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { CommonService } from '../../../../../../shared/services/common.service';
import { CreateOwnStrategyComponent } from '../create-own-strategy/create-own-strategy.component';
import { FormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageStudentDrawerParams } from '../../../../insights.interface';
import { VideoModalComponent } from '../../../../../../components/video-modal/video-modal.component';
import { PdfService } from '../../../../../../core/services/pdf.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { DrawerHeaderComponent } from '../../../../../../components/drawer-header/drawer-header.component';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { OrganizationModel } from '../../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../../organizations/organization.service';
import { loadOrganizationData } from '../../../../../../core/utils/crypto.utils';

interface ProactiveStrategyDrawerParams {
  data: StrategyItem;
  mode: 'view' | 'edit' | 'create';
  behaviorId?: number;
  caseParams: ManageStudentDrawerParams;
}

@Component({
  selector: 'app-proactive-strategy',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    NzToolTipModule,
    CommonModule,
    ConfirmDialogComponent,
    FormsModule,
    NzModalModule,
    VideoModalComponent,
    DrawerHeaderComponent,
    NzSwitchModule,
    FormsModule,
  ],
  templateUrl: './proactive-strategy.component.html',
  styleUrl: './proactive-strategy.component.scss',
})
export class ProactiveStrategyComponent implements OnDestroy {
  @ViewChild('pdfContent') pdfContent!: ElementRef;
  @ViewChild('drawerBehaviorEdit')
  drawerBehaviorEdit!: TemplateRef<void>;

  private readonly router = inject(Router);
  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly drawerService = inject(DrawerService);
  private readonly commonService = inject(CommonService);
  private readonly destroy$ = new Subject<void>();
  private readonly paramsSubject =
    new BehaviorSubject<StrategyQueryParams | null>(null);

  private originalStrategyActiveState = true;

  readonly defaultParams$ = this.paramsSubject.asObservable();
  readonly currentYear: number = new Date().getFullYear();
  readonly confrimDialogMsg = 'Are you sure you want to delete this strategy?';

  strategy?: StrategyItem;
  mode: 'view' | 'edit' | 'create' = 'view';
  showConfirmDialog = false;
  isDeleting = false;
  behaviorId?: number;

  isVisible = false;
  statusChangeReason = '';
  isStrategyActive = true;
  isStrategyId = false;
  strategyId = 0;

  isApprovalModalVisible = false;
  isApproving = false;
  approvalComments = '';
  isExpired = false;

  caseParams!: ManageStudentDrawerParams;
  orgDetail: OrganizationModel | null = null;
  logoURl = loadOrganizationData()?.logoUrl;

  isVideoVisible = false;
  loading$: Observable<boolean> = of(false);

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private pdfService: PdfService,
    private readonly loaderService: LoaderService,
    private orgService: OrganizationService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.isStrategyId = params['strategy'];
      if (params['strategy']) {
        this.isStrategyId = true;
        this.strategyId = Number(params['strategy']);
      }
    });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ProactiveStrategyDrawerParams>(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
      )
      .pipe(
        takeUntil(this.destroy$),
        filter(params => !!params?.data)
      )
      .subscribe(params => {
        if (params) {
          this.caseParams = params.caseParams;
          this.strategy = params.data;
          if (
            this.strategy?.expirationTime &&
            new Date(this.strategy.expirationTime).getTime() < Date.now()
          ) {
            this.isExpired = true;
            this.commonService.notify('info', 'Strategy link is expired');
          }
          this.behaviorId = params.behaviorId;
          this.isStrategyActive = this.strategy.statusId === 1;
          this.originalStrategyActiveState = this.isStrategyActive;
        }
        this.mode = params?.mode === 'create' ? 'create' : 'view';
      });
  }

  getOrgDetails(orgId: number) {
    this.loaderService.show();
    this.orgService
      .getOrganization(orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response) {
            this.orgDetail = response;
            if (this.orgDetail && this.orgDetail.logoUrl) {
              this.logoURl = this.orgDetail.logoUrl;
            }
          }
          this.loaderService.hide();
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  onDelete(): void {
    this.showConfirmDialog = true;
  }

  handleDelete(): void {
    if (!this.strategy?.caseBehaviorStrategyId) return;

    this.isDeleting = true;
    this.deleteStrategy();
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.isApprovalModalVisible = false;
    this.approvalComments = '';

    if (this.isVisible) {
      this.isVisible = false;
      this.statusChangeReason = '';
      this.isStrategyActive = this.originalStrategyActiveState;
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.paramsSubject.complete();
  }

  toggleStatus(): void {
    if (this.isStrategyActive) {
      this.isVisible = true;
    } else {
      this.updateStatus();
    }
  }

  updateStatus(): void {
    this.loaderService.show();

    const payload = {
      caseBehaviorStrategyId: this.strategy?.caseBehaviorStrategyId ?? 0,
      statusId: this.isStrategyActive ? 2 : 1,
      statusChageReason: this.isStrategyActive ? this.statusChangeReason : '',
    };

    const refreshStrategyList = () => {
      return this.strategyService
        .getBehaviorStrategyList({
          caseBehaviorId: this.behaviorId ?? 0,
          strategyType: this.strategy?.behaviourStrategyTypeId ?? undefined,
          pageIndex: 1,
          pageSize: 10,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.closeDrawers();
            this.loaderService.hide();
          })
        );
    };

    this.strategyService
      .updateStrategyStatus(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isVisible = false;
          this.statusChangeReason = '';
          this.isStrategyActive = !this.isStrategyActive;
          this.cdr.detectChanges();

          this.commonService.notify(
            'success',
            `Strategy ${this.isStrategyActive ? 'activated' : 'deactivated'} successfully`
          );
          this.closeDrawers();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loaderService.hide();
        },
        complete: () => {
          refreshStrategyList()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => this.loaderService.hide(),
              error: ({ errors, message }: ErrorResponse) => {
                const errorMessages = errors.map(
                  err => err.description || err.detailedMessage
                );
                this.commonService.notify('error', errorMessages, message);
              },
            });
        },
      });
  }

  closeDrawers(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
    this.drawerService.closeDrawer(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
  }

  closeManageBehaviorDetails(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
  }

  handleDeactivated(): void {
    this.isVisible = false;
    this.statusChangeReason = '';
    this.isStrategyActive = this.originalStrategyActiveState;
    this.cdr.detectChanges();
  }

  openCreateOwnStrategyComponent(): void {
    this.drawerService.openDrawer(
      CreateOwnStrategyComponent,
      this.drawerBehaviorEdit as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        strategy: this.strategy,
        behaviorId: this.behaviorId,
        mode: 'edit',
        caseParams: this.caseParams,
      },
      DRAWER_IDS.CREATE_OWN_STRATEGY
    );
  }

  openVideoModal() {
    this.isVideoVisible = true;
  }

  private deleteStrategy(): void {
    this.strategyService
      .deleteStrategy(this.strategy?.caseBehaviorStrategyId ?? 0)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
          this.showConfirmDialog = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.handleDeleteSuccess(),
        error: (error: ErrorResponse) => this.handleDeleteError(error),
      });
  }

  private handleDeleteSuccess(): void {
    this.commonService.notify('success', 'Strategy deleted successfully');
    this.strategyService
      .getBehaviorStrategyList({
        caseBehaviorId: this.behaviorId,
        strategyType: this.strategy?.behaviourStrategyTypeId ?? 0,
        pageIndex: 1,
        pageSize: 10,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.cdr.detectChanges();
          this.isDeleting = false;
        })
      )
      .subscribe({
        next: () => {
          this.drawerService.closeDrawer(
            DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
          );
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private handleDeleteError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  showApprovalModal(strategyId: number, isApprove: boolean): void {
    this.isApprovalModalVisible = true;
    this.isApproving = isApprove;
    this.strategyId = strategyId;
  }

  submitApproval(): void {
    const payload = {
      isApproved: this.isApproving,
      comments: this.approvalComments,
    };
    this.loaderService.show();

    this.strategyService.approveStrategy(this.strategyId, payload).subscribe({
      next: () => {
        this.isApprovalModalVisible = false;
        this.approvalComments = '';
        if (this.strategy) {
          this.strategy.isPublish = this.isApproving;
          this.strategy.isRejected = !this.isApproving;
        }
        this.commonService.notify(
          'success',
          `Strategy ${this.isApproving ? 'approved' : 'rejected'} successfully`
        );
        this.cdr.detectChanges();
        this.loaderService.hide();
      },
      error: error => {
        this.commonService.notify(
          'error',
          'Error updating strategy approval status'
        );
        console.error('Error updating approval status:', error);
      },
      complete: () => {
        this.isApprovalModalVisible = false;
        this.approvalComments = '';
        this.loaderService.hide();
        this.cdr.detectChanges();
      },
    });
  }

  async getStrategyPdf(): Promise<void> {
    try {
      let base64Image = '';
      if (this.logoURl) {
        base64Image = await this.commonService.getBase64ImageFromUrl(
          this.logoURl
        );
      } else {
        base64Image = await this.commonService.getBase64ImageFromUrl(
          '/assets/images/logo.png'
        );
      }

      await this.pdfService.generatePDF({
        element: this.pdfContent.nativeElement,
        fileName: `${this.strategy?.title}_Strategy.pdf`,
        excludeElements: ['.no-print', '.no-active'],
        customConfig: {
          pageSize: 'A4',
          pageOrientation: 'portrait',
        },
        printData: {
          logo: base64Image,
          orgDetail: {
            address: '6620 Acorn Dr',
            city: 'Oklahoma City',
            state: 'OK',
            country: 'USA',
            zipCode: '73151',
          },
          caseDetail: {
            student: {
              firstName: 'John',
              lastName: 'Doe',
            },
            supervisorName: 'Jane Smith',
          },
        },
      });
    } catch {
      this.commonService.notify('error', 'Failed to generate PDF');
      this.cdr.detectChanges();
    }
  }
}
